import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CustomerType } from '@worksy360/shared';
import { ApiRequestError } from '../lib/api.js';
import { useCreateCustomer, useCustomers } from '../lib/hooks.js';
import { Badge, Button, Card, Drawer, EmptyState, Field, Input, Select, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { money } from '../lib/format.js';

export function Customers() {
  const { data, isLoading, error } = useCustomers();
  const [sp, setSp] = useSearchParams();
  const [drawer, setDrawer] = useState(sp.get('new') === '1');
  const navigate = useNavigate();
  const closeDrawer = () => { setDrawer(false); if (sp.get('new')) { sp.delete('new'); setSp(sp, { replace: true }); } };
  const customers = data?.items ?? [];

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Customers</div><div className="page-sub">{customers.length} {customers.length === 1 ? 'customer' : 'customers'}</div></div>
        <Button onClick={() => setDrawer(true)}>+ New customer</Button>
      </div>

      <Card>
        {isLoading ? (
          <div className="card-pad stack gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} h={22} />)}</div>
        ) : error ? (
          <EmptyState icon="⚠️" title="Couldn't load customers">Is the API running?</EmptyState>
        ) : customers.length === 0 ? (
          <EmptyState icon="☺" title="No customers yet" action={<Button onClick={() => setDrawer(true)}>Add your first customer</Button>}>
            Customers you add here become the hub for estimates, jobs, invoices and payments.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Type</th><th>Contact</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ textAlign: 'right' }}>Lifetime</th></tr></thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)}>
                    <td><strong>{c.displayName}</strong></td>
                    <td><Badge tone={c.type === 'commercial' ? 'info' : 'neutral'}>{c.type}</Badge></td>
                    <td className="muted">{c.phones?.[0] ?? c.emails?.[0] ?? '—'}</td>
                    <td className="tabular" style={{ textAlign: 'right', color: c.outstandingBalanceCents ? 'var(--color-danger)' : 'inherit' }}>{money(c.outstandingBalanceCents)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{money(c.lifetimeValueCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {drawer && <NewCustomerDrawer onClose={closeDrawer} onCreated={(id) => navigate(`/customers/${id}`)} />}
    </>
  );
}

function NewCustomerDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const create = useCreateCustomer();
  const toast = useToast();
  const [displayName, setName] = useState('');
  const [type, setType] = useState<CustomerType>('residential');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    setFieldErrors({});
    try {
      const customer = await create.mutateAsync({
        displayName,
        type,
        phones: phone ? [phone] : undefined,
        emails: email ? [email] : undefined,
      });
      toast.push('Customer created', 'success');
      onCreated(customer.id);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setFieldErrors(err.fields ?? {});
        toast.push(err.message, 'error');
      }
    }
  };

  return (
    <Drawer
      title="New customer"
      onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={create.isPending} onClick={submit}>Create customer</Button></>}
    >
      <div className="stack gap-4">
        <Field label="Name" error={fieldErrors.displayName}><Input value={displayName} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Homeowner or Acme Offices" invalid={!!fieldErrors.displayName} autoFocus /></Field>
        <Field label="Type"><Select value={type} onChange={(e) => setType(e.target.value as CustomerType)}><option value="residential">Residential</option><option value="commercial">Commercial</option></Select></Field>
        <Field label="Phone" error={fieldErrors.phones}><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" inputMode="tel" /></Field>
        <Field label="Email" error={fieldErrors.emails}><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" type="email" /></Field>
      </div>
    </Drawer>
  );
}
